import type { SheetData } from 'write-excel-file/browser'

// Spreadsheet, Word and print helpers for the export and import buttons.
// Browser-only: call these from event handlers, never during render.

export type SheetCell = {
    value: string | number | null | undefined
    header?: boolean
    columnSpan?: number
    rowSpan?: number
    align?: 'left' | 'center' | 'right'
} | null

type XlsxOptions = {
    sheet?: string
    columns?: number[]
    headerRows?: number
}

export const downloadBlob = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 1500)
}

export const downloadXlsx = async (fileName: string, rows: SheetCell[][], options: XlsxOptions = {}): Promise<void> => {
    const { default: writeXlsxFile } = await import('write-excel-file/browser')

    const data = rows.map(row => row.map(cell => {
        if (cell === null) return null

        const numeric = typeof cell.value === 'number'

        return {
            value: cell.value ?? '',
            type: numeric ? Number : String,
            fontWeight: cell.header ? 'bold' as const : undefined,
            backgroundColor: cell.header ? '#E8EEF9' : undefined,
            align: cell.align ?? (cell.header ? 'center' as const : numeric ? 'center' as const : 'left' as const),
            alignVertical: 'center' as const,
            columnSpan: cell.columnSpan,
            rowSpan: cell.rowSpan,
            borderStyle: 'thin' as const,
            borderColor: '#94A3B8',
            wrap: true
        }
    }))

    await writeXlsxFile(data as SheetData, {
        sheet: options.sheet ?? 'Data',
        columns: options.columns?.map(width => ({ width })),
        stickyRowsCount: options.headerRows
    }).toFile(fileName)
}

const escapeHtml = (value: string): string => {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
}

// A Word-compatible document (.doc). Styles are inline so the file needs no document head.
export const downloadWordTable = (fileName: string, title: string, subtitle: string, rows: SheetCell[][]): void => {
    const cellStyle = 'border:1px solid #94a3b8;padding:4px 6px;font-family:Calibri,Arial,sans-serif;font-size:10pt;vertical-align:middle;'
    const body = rows.map(row => {
        const cells = row.map(cell => {
            if (cell === null) return ''

            const tag = cell.header ? 'th' : 'td'
            const span = `${cell.columnSpan ? ` colspan="${cell.columnSpan}"` : ''}${cell.rowSpan ? ` rowspan="${cell.rowSpan}"` : ''}`
            const align = cell.align ?? (cell.header || typeof cell.value === 'number' ? 'center' : 'left')
            const background = cell.header ? 'background:#e8eef9;font-weight:bold;' : ''

            return `<${tag}${span} style="${cellStyle}${background}text-align:${align};">${escapeHtml(String(cell.value ?? ''))}</${tag}>`
        }).join('')

        return `<tr>${cells}</tr>`
    }).join('')

    const html = [
        '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">',
        '<body style="font-family:Calibri,Arial,sans-serif;">',
        `<h2 style="font-family:Calibri,Arial,sans-serif;font-size:14pt;margin:0 0 4px;">${escapeHtml(title)}</h2>`,
        `<p style="font-family:Calibri,Arial,sans-serif;font-size:10pt;color:#475569;margin:0 0 12px;">${escapeHtml(subtitle)}</p>`,
        `<table style="border-collapse:collapse;width:100%;">${body}</table>`,
        '</body></html>'
    ].join('')

    downloadBlob(new Blob(['﻿', html], { type: 'application/msword' }), fileName)
}

const parseCsv = (text: string): string[][] => {
    const firstLine = text.split(/\r?\n/, 1)[0] ?? ''
    const delimiter = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ';' : ','
    const rows: string[][] = []
    let row: string[] = []
    let field = ''
    let quoted = false

    for (let index = 0; index < text.length; index += 1) {
        const char = text[index]

        if (quoted) {
            if (char === '"' && text[index + 1] === '"') {
                field += '"'
                index += 1
            }
            else if (char === '"') {
                quoted = false
            }
            else {
                field += char
            }

            continue
        }

        if (char === '"') quoted = true
        else if (char === delimiter) {
            row.push(field)
            field = ''
        }
        else if (char === '\n' || char === '\r') {
            if (char === '\r' && text[index + 1] === '\n') index += 1

            row.push(field)
            rows.push(row)
            row = []
            field = ''
        }
        else {
            field += char
        }
    }

    if (field !== '' || row.length > 0) {
        row.push(field)
        rows.push(row)
    }

    return rows.map(cells => cells.map(cell => cell.replace(/^﻿/, '').trim()))
}

const parseSpreadsheetXml = (text: string): string[][] => {
    const documentNode = new DOMParser().parseFromString(text, 'application/xml')
    const rows = Array.from(documentNode.getElementsByTagNameNS('*', 'Row'))

    return rows.map(row => {
        const cells: string[] = []

        Array.from(row.children).filter(cell => cell.localName === 'Cell').forEach(cell => {
            const indexAttribute = Array.from(cell.attributes).find(attribute => attribute.localName === 'Index')

            if (indexAttribute) {
                while (cells.length < Number(indexAttribute.value) - 1) cells.push('')
            }

            const data = Array.from(cell.children).find(child => child.localName === 'Data')
            cells.push((data?.textContent ?? '').trim())
        })

        return cells
    })
}

const cellToText = (cell: unknown): string => {
    if (cell === null || cell === undefined) return ''
    if (cell instanceof Date) return cell.toISOString().slice(0, 10)

    return String(cell).trim()
}

export const readSpreadsheetFile = async (file: File): Promise<string[][]> => {
    const extension = file.name.split('.').pop()?.toLowerCase()

    if (extension === 'csv') return parseCsv(await file.text())
    if (extension === 'xml') return parseSpreadsheetXml(await file.text())

    if (extension === 'xlsx') {
        const { readSheet } = await import('read-excel-file/browser')
        const rows = await readSheet(file)

        return rows.map(row => row.map(cellToText))
    }

    throw new Error('Format tidak didukung. Gunakan CSV, XML Spreadsheet, atau Excel (.xlsx).')
}

export const printPage = (): void => {
    window.print()
}
